import React, { useState, useEffect } from "react";
import { TakeFiveRiskTypeEnumToString } from "../../../shared/enums/take-five-risk-type-enum";
import { RiskTagEnumToString } from "../../../shared/enums/take-five-risk-tag-enum";
import { Hazard } from "../../../shared/models/api/models";
import { getHazards, getRisks, getRiskAssessmentMatrix } from "../../../shared/services/railOperationalFormsApi";
import {
    takeFiveFormState as t,
    TakeFiveFormState
} from "./TakeFiveFormState";
import Modal from "./Modal";
import { Button } from "reactstrap";

interface HazardCardProps {
    item: Hazard;
    readonly?: boolean;
    onEdit?: (hazard: Hazard) => void;
}

interface ThumbnailProps {
    file: File | undefined; // Image file in memory
    maxWidth?: string;
}

export const Thumbnail = (props: ThumbnailProps) => {
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!props.file) {
            setPreviewUrl(null);
            return;
        }

        // Create object URL for preview
        const objectUrl = URL.createObjectURL(props.file);
        setPreviewUrl(objectUrl);

        // Cleanup to avoid memory leaks
        return () => {
            URL.revokeObjectURL(objectUrl);
        };

    }, [props.file]);

    if (!props.file) return null;

    return previewUrl ? (
        <img
            src={previewUrl}
            alt="Thumbnail preview"
            style={{
                width: `${props.maxWidth}`,
                aspectRatio: 1,
                height: "auto",
                border: "1px solid #ccc",
                borderRadius: "4px",
            }}
        />
    ) : (
        <div style={{
            width: `${props.maxWidth}`,
            aspectRatio: 1,
            border: "1px solid #ccc",
            borderRadius: "4px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f5f5f5"
        }}>Loading image...</div>
    );
}

export const HazardCard = (props: HazardCardProps) => {
    const [backgroundColor, setBackgroundColor] = useState('');
    const [riskTag, setRiskTag] = useState('' as string | undefined);
    const [formattedScore, setFormattedScore] = useState('');
    const [hazardLookup, setHazardLookup] = useState([] as { id: number, hazard: string }[]);
    const [riskLookup, setRiskLookup] = useState([] as { id: number, risk: string }[]);
    const [hazardDetail, setHazardDetail] = useState('');
    const [riskDetail, setRiskDetail] = useState('');
    const [loading, setLoading] = useState(false);

    // Modal
    const [open, setOpen] = useState(false);
    const showModal = () => setOpen(true);
    const hideModal = () => setOpen(false);

    useEffect(() => {
        const loadRiskAssessmentData = async () => {
            setLoading(true);
            const matrix = await getRiskAssessmentMatrix();
            const riskAssessment = matrix.filter(r => r.score === Number(props.item.riskScore));
            if (riskAssessment.length > 0) {
                setBackgroundColor(riskAssessment[0].color);
                setFormattedScore('(' + props.item.riskScore + ')');
                setRiskTag(RiskTagEnumToString(riskAssessment[0].tag));
            }
            setLoading(false);
        };

        loadRiskAssessmentData();
    }, [props.item.riskScore]);

    useEffect(() => {
        const loadLookupData = async () => {
            setLoading(true);
            const hazards = await getHazards();
            setHazardLookup(hazards);
            const risks = await getRisks();
            setRiskLookup(risks);
            setLoading(false);
        };

        loadLookupData();
    }, []);

    useEffect(() => {
        if (hazardLookup.length > 0 && riskLookup.length > 0) {
            const hazard = hazardLookup.find(h => h.id === props.item.hazardId);
            if (hazard) {
                setHazardDetail(hazard.hazard);
            }
            const risk = riskLookup.find(r => r.id === props.item.riskId);
            if (risk) {
                setRiskDetail(risk.risk);
            }
        }
    }, [hazardLookup, riskLookup, props.item.hazardId, props.item.riskId]);

    const showImage = () => {
        setOpen(true);
    };

    const deleteItem = () => {
        t.setState(s => { s.form.taskHazards = s.form.taskHazards?.filter(h => h !== props.item) });
    }

    const editItem = () => {
        if (props.onEdit) {
            props.onEdit(props.item);
        }
    }

    return (
        <div className="hazard-item">
            <div className="hazard-item-entry">
                <div className="hazard-item-entry-label">Hazard:</div>
                <div className="hazard-item-entry-value">
                    {props.item.hazardId === -2 || !props.item.hazardId ? props.item.otherHazard : hazardDetail}
                </div>
            </div>
            <div className="hazard-item-entry">
                <div className="hazard-item-entry-label">{TakeFiveRiskTypeEnumToString(Number(props.item.riskType!))}:</div>
                <div className="hazard-item-entry-value">{riskDetail}</div>
            </div>
            <div className="hazard-item-entry">
                <div className="hazard-item-entry-label">Risk Score:</div>
                <div className="hazard-item-entry-value" style={{ backgroundColor }}>{riskTag} {formattedScore}</div>
            </div>
            <div className="hazard-controls">
                <div className="hazard-label-image">
                    <div className="controls-item-entry-label">Controls:</div>
                    <div style={{ marginTop: "10px" }} onClick={ showImage }>
                        <Thumbnail file={props.item.file} maxWidth="90%" />
                    </div>
                </div>
                <div className="hazard-item-entry-value">{props.item.control}</div>
            </div>

            {!props.readonly &&
                <div className="submit btns-container">
                    <Button className="btn btn-secondary" style={{ backgroundColor: '#333', color: 'white' }} onClick={editItem}>Edit</Button>
                    <Button className="btn btn-danger" onClick={deleteItem}>Delete</Button>
                </div>
            }

            <Modal open={open} onClose={hideModal} maxWidth="30%">
                <Thumbnail file={props.item.file} maxWidth="100%" />
                <div className="submit btns-container">
                    <Button className="btn-primary" onClick={ hideModal }>Close</Button>
                </div>
            </Modal>
        </div>
    );
}